import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Loader2, CreditCard, ExternalLink, Sparkles, ArrowUpCircle, AlertTriangle, OctagonAlert } from "lucide-react";
import CurrentPlanCard from "./billing/CurrentPlanCard";
import PaidAddonsCard from "./billing/PaidAddonsCard";
import TransactionsTable from "./billing/TransactionsTable";
import ChangePlanModal from "./billing/ChangePlanModal";
import SolunaryServicesCard from "./billing/SolunaryServicesCard";
import BillingBreakdownCard from "./billing/BillingBreakdownCard";
import { PurchaseCatalogCard } from "./billing/PurchaseCatalogCard";
import TrialCard from "./billing/TrialCard";
import PlanUsageCard from "./billing/PlanUsageCard";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { TRIAL_PATHS } from "../saas/trial/contract";
import { billingErrorMessage } from "../lib/billingErrors";
import { apiErrorCode } from "../lib/apiErrors";
import PlanChangeReview from "../platformOffers/PlanChangeReview";
import BillingRedeemCard from "../platformOffers/BillingRedeemCard";
import TenantRedemptionsCard from "../platformOffers/TenantRedemptionsCard";
import useTenantRedemptions from "../platformOffers/useTenantRedemptions";
import { isPlatformOfferError, platformOfferErrorMessage } from "../platformOffers/errors";

/**
 * BillingAdmin — orchestration only.
 * Avant B17 : 237 lignes monolithiques (complexité 32).
 * Après : 90 lignes + 4 sous-composants (CurrentPlanCard, PaidAddonsCard, TransactionsTable, ChangePlanModal).
 */
export default function BillingAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  // The plan picked in the modal, awaiting confirmation (and an optional promo code) in
  // PlanChangeReview. Nothing changes until that step is confirmed.
  const [targetPlan, setTargetPlan] = useState(null);
  const redemptions = useTenantRedemptions();
  // A plan change or an add-on purchase changes what the practice owns: the capability
  // map is re-read so the sidebar and the route guards follow without a page reload.
  const { reload: reloadEntitlements } = useEntitlements();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get(TRIAL_PATHS.billingSummary),
        api.get("/saas/plans"),
      ]);
      setSummary(s.data);
      setPlans(p.data);
    } catch (e) {
      toast.error(extractApiError(e, t("bill.error")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openPortal = async () => {
    try {
      const { data } = await api.post("/tenant/billing/portal");
      if (data.demo_mode) {
        toast.info(t("bill.demoPortal"));
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(billingErrorMessage(e, t, t("bill.stripeError")));
    }
  };

  const choosePlan = (planId) => {
    setTargetPlan(plans.find((p) => p.plan_id === planId) || null);
    setShowPlanModal(false);
  };

  const changePlan = async (promoCode) => {
    setChanging(true);
    try {
      const r = await api.post("/tenant/billing/change-plan", {
        plan_id: targetPlan.plan_id,
        currency: summary?.tenant?.currency,
        promo_code: promoCode,
      });
      // The server says whether the promo was attached or scheduled for the end of the
      // trial (contract §5.0/§5.3): `redemption` is null when no code was sent.
      const status = r?.data?.redemption?.status;
      const sentence = t(!status ? "bill.planUpdated"
        : status === "scheduled" ? "bill.offers.planUpdatedPromoScheduled"
          : "bill.offers.planUpdatedPromo");
      // A plan change drops the negotiated price (contract D4 §10.1): say so. Without a
      // promo the dedicated sentence replaces "Plan updated"; with one it is appended.
      const ended = r?.data?.custom_price_ended === true;
      toast.success(!ended ? sentence
        : status ? `${sentence} ${t("bill.customPrice.endedByPlanChange")}`
          : t("bill.customPrice.endedByPlanChange"));
      setTargetPlan(null);
      load();
      reloadEntitlements();
      if (promoCode) redemptions.reload();
    } catch (e) {
      toast.error(isPlatformOfferError(e)
        ? platformOfferErrorMessage(e, tc, t("bill.error"))
        : billingErrorMessage(e, t, t("bill.error")));
      if (apiErrorCode(e) === "PLAN_CHANGE_SUPERSEDED") {
        // A newer billing change owns the practice's plan now: the review step describes
        // a change that no longer applies, so it closes and the page re-reads what is in force.
        setTargetPlan(null);
        load();
        reloadEntitlements();
      }
    } finally {
      setChanging(false);
    }
  };

  if (loading || !summary) return <Loader2 className="w-6 h-6 animate-spin text-eh-muted" />;

  const { tenant, plan = {}, recent_transactions: txns = [], billing_breakdown, custom_price: customPrice = null, dunning = {}, trial = { state: "none" } } = summary;

  // Bannière d'impayé — DEUX états distincts, et les confondre serait mentir :
  //   `past_due`  → rien n'est coupé, il reste N jours (avertissement, ambre) ;
  //   `suspended` → c'est déjà coupé (constat, rose).
  // `days_remaining` vient du backend : lui seul détient la fenêtre de grâce et l'horloge
  // de référence, et deux arithmétiques de dates finissent toujours par divergier.
  const daysLeft = dunning.days_remaining;
  const dunningBanner = dunning.past_due
    ? {
        testid: "dunning-past-due-banner",
        tone: "bg-amber-50 border-amber-300 text-amber-900",
        Icon: AlertTriangle,
        title: t("bill.dunning.title"),
        body: daysLeft > 0 ? t("bill.dunning.body", { days: daysLeft }) : t("bill.dunning.lastDay"),
      }
    : dunning.suspended_for_non_payment
      ? {
          testid: "dunning-suspended-banner",
          tone: "bg-rose-50 border-rose-300 text-rose-900",
          Icon: OctagonAlert,
          title: t("bill.dunning.suspendedTitle"),
          body: t("bill.dunning.suspendedBody"),
        }
      : null;

  return (
    <div data-testid="billing-admin" className="space-y-6 max-w-5xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-sol-gold" /> {t("bill.title")}
          </h1>
          <p className="text-eh-muted text-sm mt-1">{t("bill.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowPlanModal(true)} variant="outline" data-testid="change-plan-btn">
            <ArrowUpCircle className="w-4 h-4 mr-2" /> {t("bill.changePlan")}
          </Button>
          <Button onClick={openPortal} data-testid="open-portal-btn" className="bg-eh-ink text-eh-bg">
            <ExternalLink className="w-4 h-4 mr-2" /> {t("bill.stripePortal")}
          </Button>
        </div>
      </div>

      {dunningBanner && (
        <div
          data-testid={dunningBanner.testid}
          className={`border rounded-2xl p-4 text-sm flex items-start gap-3 ${dunningBanner.tone}`}
        >
          <dunningBanner.Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <strong className="block">{dunningBanner.title}</strong>
            <p className="mt-1">{dunningBanner.body}</p>
            {dunning.attempts > 1 && (
              <p className="mt-1 opacity-70 text-xs">
                {t("bill.dunning.attempts", { n: dunning.attempts })}
              </p>
            )}
            <Button
              onClick={openPortal}
              data-testid="dunning-portal-btn"
              size="sm"
              className="mt-3 bg-eh-ink text-eh-bg"
            >
              <CreditCard className="w-4 h-4 mr-2" /> {t("bill.dunning.cta")}
            </Button>
          </div>
        </div>
      )}

      {summary.demo_mode && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex items-start gap-3">
          <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>{t("bill.demoTitle")}</strong>{t("bill.demoText")}
          </div>
        </div>
      )}

      <TrialCard trial={trial} onManageCard={openPortal} t={t} />
      <CurrentPlanCard tenant={tenant} plan={plan} trial={trial} customPrice={customPrice} t={t} />
      <BillingBreakdownCard breakdown={billing_breakdown} customPrice={customPrice} lang={lang} t={t} />
      <PlanUsageCard t={t} />
      <BillingRedeemCard
        tenant={tenant}
        trialing={trial.state === "trialing"}
        onRedeemed={redemptions.reload}
        t={t}
      />
      <TenantRedemptionsCard redemptions={redemptions} t={t} />
      <PurchaseCatalogCard onPurchased={() => { load(); reloadEntitlements(); }} t={t} />
      <PaidAddonsCard lines={billing_breakdown?.addons} t={t} />
      <SolunaryServicesCard t={t} />
      <TransactionsTable txns={txns} t={t} />

      <ChangePlanModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        plans={plans}
        tenant={tenant}
        onChoose={choosePlan}
        changing={changing}
        t={t}
      />
      {targetPlan && (
        <PlanChangeReview
          plan={targetPlan}
          tenant={tenant}
          trialing={trial.state === "trialing"}
          customPrice={customPrice}
          changing={changing}
          onConfirm={changePlan}
          onCancel={() => setTargetPlan(null)}
          t={t}
        />
      )}
    </div>
  );
}
